<template>
  <!-- #region Cart item -->
  <div v-for="item of $cartStore.items" class="cart-item card my-3" :key="`card-${item.id}`">
    <div class="d-flex flex-nowrap">
      <div class="cart-img">
        <img :src="imageForCard(item)" class="p-3" />
      </div>
      <div class="card-body ps-0 d-flex flex-column">
        <div class="cart-item-title">{{ item.productVariant.name }}</div>
        <div class="card-item-price mb-3">
          <div class="d-flex justify-content-between flex-grow-1" v-if="!isDonation(item)">
            <div class="text-nowrap"
              :class="{
                'text-ss-grey': !$cartStore.hasItemDiscount(item),
                'text-ss-grey-light': $cartStore.hasItemDiscount(item),
                'text-decoration-line-through': $cartStore.hasItemDiscount(item),
              }"
            >
              {{ $cartStore.formatPrice(item.unitPrice) }} / ea
            </div>
            <div>
              <a v-if="$cartStore.activeOrder?.state === 'AddingItems'"
                href="#"
                color="btn btn-text text-ss-iris text-decoration-underline"
                @click.prevent="$cartStore.removeFromCart(item.id)"
              ><i class="fa-duotone fa-trash pe-2"></i>Remove</a>
            </div>
          </div>
          <div v-if="$cartStore.hasItemDiscount(item)">
            <div class="text-nowrap text-ss-grey">{{ $cartStore.formatPrice($cartStore.discountedItemPrice(item)) }} / ea</div>
            <div class="d-flex flex-wrap gap-1">
              <span v-for="(discount, i) of $cartStore.getItemDiscounts(item)" :key="i"
                class="badge rounded-pill bg-primary"
              >
                <i class="fa fa-tag me-1"></i>{{ discount.description }}
              </span>
            </div>
          </div>
        </div>
        <div class="cart-item-quantity d-flex justify-content-between align-items-center" v-if="!isDonation(item)">
          <div>
            <CInputGroup>
              <button
                class="btn btn-outline-grey"
                :disabled="$cartStore.isLoading > 0 || $cartStore.activeOrder?.state !== 'AddingItems'"
                @click="adjustQuantity(item.id, -1)"
              >
                <i class="fa-solid fa-minus"></i>
              </button>
              <input
                class="form-control p-1 text-center w-4rem"
                type="text"
                v-model.lazy.number="adjustedQuantities[item.id]"
                :disabled="$cartStore.isLoading > 0 || $cartStore.activeOrder?.state !== 'AddingItems'"
                :class="{'border-warning': qUpdatePending(item.id), 'border-ss-grey-dark': !qUpdatePending(item.id)}"
              />
              <button
                class="btn btn-outline-grey"
                :disabled="$cartStore.isLoading > 0 || $cartStore.activeOrder?.state !== 'AddingItems'"
                @click.prevent="adjustQuantity(item.id, 1)"
              >
                <i class="fa-solid fa-plus fs-normal"></i>
              </button>
            </CInputGroup>
          </div>
          <div class="ms-3 text-nowrap">
            <strong class="text-ss-grey-dark fs1_5"
              :class="{'text-ss-grey-light': qUpdatePending(item.id)}"
              >{{ $cartStore.formatPrice($cartStore.discountedLinePrice(item)) }}</strong>
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center flex-grow-1" v-else>
          <div>
            <div class="input-group" id="donate-input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">$</span>
              </div>
              <input
                type="number"
                class="form-control"
                min="0"
                step="1.00"
                aria-label="Donation amount"
                placeholder="0.00"
                v-model.lazy.number="adjustedDonations[item.id]"
                @keyup="sanitizeDonation(item.id)"
                @blur="sanitizeDonation(item.id)"
                :disabled="$cartStore.isLoading > 0 || $cartStore.activeOrder?.state !== 'AddingItems'"
              >
            </div>
          </div>
          <div class="flex-shrink-0">
            <a v-if="$cartStore.activeOrder?.state === 'AddingItems'"
              href="#"
              color="btn btn-text text-ss-iris text-decoration-underline"
              @click.prevent="$cartStore.removeFromCart(item.id)"
            ><i class="fa-duotone fa-trash pe-2"></i>Remove</a>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="!$cartStore.items?.length"
    class="text-center py-5 my-5 text-ss-grey"
  >
    No items in your cart
    <br />
    <i class="fa-duotone fa-tower-cell fa-xl fa-beat-fade my-4"></i>
  </div>
</template>

<script lang="ts" setup>

import {
  CInputGroup,
} from '@coreui/bootstrap-vue/src/components/form/index';

import { OrderType, useCartStore } from './cartStore';
import { computed, ref, watch, watchEffect } from 'vue';

import debounce from 'lodash/debounce';

// When you change a quantity, it changes "adjustedQuantities" immediately, but
// doesn't save it to the server. Once it saves then it will match up again
const adjustedQuantities = ref({} as Record<string, number>);
function adjustQuantity(id: string, delta: number, absolute = false) {
  const adjustedValue = absolute ? delta : adjustedQuantities.value[id] + delta;

  // Value must be between 0 and 300
  let newQuantity = Math.min(
    Math.max( adjustedValue, 1 ),
    300
  );
  adjustedQuantities.value[id] = newQuantity;
}

const quantityMap = computed(() => {
  const items = $cartStore.items;
  const quantityMap = Object.fromEntries(items.map((item) => [item.id, item.quantity]));
  return quantityMap;
});

const $cartStore = useCartStore();

const debounceUpdateQty = debounce(async () => {
  try {
    // Find a list of all updated quantities
    const updatedQuantities = Object.entries(adjustedQuantities.value)
      .filter(([id, quantity]) => quantity !== quantityMap.value[id])
      .map(([id, quantity]) => ({ id, quantity }));

    if (updatedQuantities.length === 0) {
      return;
    }

    await $cartStore.updateItemQuantities(updatedQuantities);
  } catch (err) {
    console.warn("Error updating cart:", err);
  }
}, 500);

// converted from 325 -> "3.25"
const adjustedDonations = ref({} as Record<string, string>);
const donationMap = computed(() => {
  const items = $cartStore.items.filter(isDonation);
  const mapped: Record<string, number> = Object.fromEntries(items.map((item) => [item.id, item.unitPrice]));
  return mapped;
});
const debounceUpdateDonations = debounce(async () => {
  try {
    const updatedDonations = Object.entries(adjustedDonations.value)
      .map(([id, donation]) => [id, Math.floor(Number(donation) * 100)] as [string, number])
      .filter(([id, donation]) => donation !== donationMap.value[id]);

    if (!updatedDonations.length) return;
    // it should be almost impossible to get here with multiple donations to update,
    // so I'm not bothering to put in bulk update functionality
    for (const [id, donation] of updatedDonations) {
      const productVariantId = $cartStore.items.find((item) => item.id === id)?.productVariant.id;
      if (!productVariantId) continue;
      await $cartStore.updateDonationAmount(productVariantId, donation);
    }

  } catch (err) {
    console.warn("Error updating cart:", err);
  }
}, 500);

function sanitizeDonation(id: string) {
  const val = Number(adjustedDonations.value[id]);
  if (isNaN(val) || val < 0) {
    adjustedDonations.value[id] = '0.00';
  } else {
    // just using toFixed would change 3.539 to 3.54
    adjustedDonations.value[id] = (Math.floor(val * 100) / 100).toFixed(2);
  }
}

function imageForCard(item: OrderType['lines'][0]) {
  return item.productVariant.featuredAsset?.source
    || item.productVariant.product.featuredAsset?.source;
}

function qUpdatePending(id: string) {
  return (adjustedQuantities.value[id] !== quantityMap.value[id]);
}

function isDonation(item: OrderType['lines'][0]) {
  return item?.productVariant?.customFields?.digitalProductType === 'Donation';
}

watch(adjustedQuantities, debounceUpdateQty, { deep: true });
watch(adjustedDonations, debounceUpdateDonations, { deep: true });
watchEffect(() => {
  adjustedQuantities.value = Object.fromEntries(Object.entries(quantityMap.value).map(([id, quantity]) => [id, quantity]));
  adjustedDonations.value = Object.fromEntries(Object.entries(donationMap.value).map(
    ([id, quantity]) => [id, (quantity / 100).toFixed(2)]
  ));
});

</script>

<style lang="scss" scoped>
</style>